/**
 * The two directions of the "add conversion leg" form, as pure functions.
 *
 * `fxRate` in this system is always **rubles per one unit of the paid
 * currency** (`computeRubTotal` falls back to `paidAmount × fxRate`), so
 *
 *   · typing the ₽ the bank charged implies  fxRate = rubAmount ÷ paidAmount
 *   · typing the rate first implies         rubAmount = fxRate × paidAmount
 *
 * Used by `ConversionLegModal` to fill the field the operator did not type and
 * to show the "≈ ₽… per USD" helper line.
 */

export interface ConversionDerivation {
  /** ₽ per one unit of the paid currency, or null when it cannot be derived. */
  fxRate: number | null;
  /** The rubles the leg books, or null when only the rate is unknown. */
  rubAmount: number | null;
  /** The one-line helper for the form, or null when there is nothing to show. */
  hint: string | null;
}

const TWO = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SIX = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 6 });

/** `83.4226` -> `83,4226` — matches the DTO's `@IsNumber({maxDecimalPlaces: 6})`. */
function fmt(value: number, formatter: Intl.NumberFormat): string {
  return Number.isFinite(value) ? formatter.format(value) : '—';
}

/**
 * @param paidAmount   the event's `paidAmount` (what the merchant charged)
 * @param paidCurrency the event's `paidCurrency`
 * @param amount       what the operator typed as the leg's ₽ amount
 * @param fxRate       what the operator typed as the leg's FX rate
 */
export function deriveConversionRate(params: {
  paidAmount: number;
  paidCurrency: string;
  amount: number | null;
  fxRate: number | null;
}): ConversionDerivation {
  const { paidAmount, paidCurrency } = params;
  const amount = params.amount !== null && Number.isFinite(params.amount) && params.amount > 0 ? params.amount : null;
  const fxRate = params.fxRate !== null && Number.isFinite(params.fxRate) && params.fxRate > 0 ? params.fxRate : null;
  const paid = paidAmount > 0 ? paidAmount : null;

  // The rate wins: it is the fact the bank printed, the amount follows from it.
  if (fxRate !== null) {
    const rubAmount = paid !== null ? roundMoney(fxRate * paid) : null;
    return {
      fxRate,
      rubAmount,
      hint:
        `≈ ₽${fmt(rubAmount ?? 0, TWO)} for ${paid !== null ? fmt(paid, TWO) : '—'} ${paidCurrency}` +
        ` · ${fmt(fxRate, SIX)} ₽ per ${paidCurrency}`,
    };
  }

  if (amount !== null && paid !== null) {
    const derived = round6(amount / paid);
    return {
      fxRate: derived,
      rubAmount: amount,
      hint: `≈ ${fmt(derived, SIX)} ₽ per ${paidCurrency}`,
    };
  }

  return { fxRate: null, rubAmount: null, hint: null };
}

/** Same nudge `roundMoney` uses in the API: money leaves with 2 decimals. */
function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function round6(value: number): number {
  return Math.round((value + Number.EPSILON) * 1_000_000) / 1_000_000;
}
